import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { aboutData, affiliationsData } from '../data/siteData';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

// New about page image
const aboutImage = "https://customer-assets.emergentagent.com/job_content-hub-634/artifacts/wk3i6380_Gaurav%20Shekhar.jpg";

// Updated bio content
const aboutBio = `A friend asked me what I am passionate about. "Serving people by connecting them" was the answer.

I stand at the intersection where industry innovation meets academic excellence. As the Associate Dean for Administration, Student Success, and Alumni Relations at the Naveen Jindal School of Management, The University of Texas at Dallas, I lead strategic initiatives while serving as the Academic Director for one of the world's largest Graduate Business Analytics programs.

My approach to education is rooted in reality. With over 15 years of experience as a Technologist and Academic, trained in computer science, IT management, public policy, and political economy, I bring a practical edge to the classroom. Whether teaching Robotic Process Automation and Business Intelligence as an associate professor or sharing insights through corporate or community outreach, I strive to bridge the gap between technical complexity and strategic execution.

Beyond academia, I am a Distinguished Toastmaster, a Senior Member of IEEE, and a proud honoree of D CEO's "Dallas 500" (2024) and the Dallas Business Journal's "40 Under 40" (2023). My mission remains constant: to empower the next generation of professionals to navigate the digital revolution with confidence.`;

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-brand-secondary/20 to-transparent rounded-3xl blur-2xl" />
                <img
                  src={aboutImage}
                  alt="Gaurav Shekhar"
                  className="relative w-full max-w-lg mx-auto rounded-2xl shadow-xl"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              className="order-1 lg:order-2"
              initial="initial"
              animate="animate"
              variants={{
                animate: { transition: { staggerChildren: 0.1 } },
              }}
            >
              <motion.p
                variants={fadeInUp}
                className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4"
              >
                About Me
              </motion.p>
              <motion.h1
                variants={fadeInUp}
                className="font-heading text-4xl md:text-6xl font-bold text-brand-primary leading-tight mb-6"
              >
                {aboutData.headline}
              </motion.h1>
              <motion.p
                variants={fadeInUp}
                className="text-brand-secondary text-xl font-medium mb-6"
              >
                {aboutData.subheadline}
              </motion.p>
              
              {/* CV Button */}
              <motion.div variants={fadeInUp}>
                <Button
                  asChild
                  className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 px-6"
                >
                  <Link to="/cv">
                    <FileText className="w-4 h-4 mr-2" />
                    Full Curriculum Vitae
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-20 bg-brand-accent">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            {aboutBio.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-slate-600 leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 grid grid-cols-2 gap-6 md:gap-8"
          >
            {aboutData.stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 bg-white rounded-2xl shadow-sm"
              >
                <p className="font-heading text-3xl md:text-4xl font-bold text-brand-secondary mb-2">
                  {stat.value}
                </p>
                <p className="text-slate-600 text-sm">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Affiliations Section - Now supports logos */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary mb-4">
              Professional Affiliations
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Proud to be associated with these organizations and communities.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {affiliationsData.map((affiliation, index) => (
              <motion.div
                key={affiliation.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-brand-accent rounded-2xl hover:shadow-lg transition-shadow text-center"
              >
                {/* Logo or fallback icon */}
                {affiliation.logo ? (
                  <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                    <img 
                      src={affiliation.logo} 
                      alt={affiliation.name}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-brand-secondary/10 flex items-center justify-center mx-auto mb-4">
                    <span className="text-brand-secondary font-bold text-2xl">
                      {affiliation.name.charAt(0)}
                    </span>
                  </div>
                )}
                <h3 className="font-semibold text-brand-primary mb-1 text-sm">
                  {affiliation.name}
                </h3>
                <p className="text-brand-secondary text-xs">{affiliation.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
